/**
 * EventCalendar Component
 * Displays upcoming events in a calendar/timeline view
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Spinner, Alert, Form, Badge } from 'react-bootstrap';

import { useOpportunityApi } from '@/shared/services/opportunityApi';

import '../Opportunities.css';

// Group events by date
function groupEventsByDate(events) {
    const grouped = {};

    events.forEach((event) => {
        const date = new Date(event.dates?.startDate);
        const dateKey = date.toISOString().split('T')[0];

        if (!grouped[dateKey]) {
            grouped[dateKey] = {
                date,
                events: [],
            };
        }
        grouped[dateKey].events.push(event);
    });

    // Sort by date
    return Object.entries(grouped)
        .sort(([a], [b]) => new Date(a) - new Date(b))
        .map(([key, value]) => ({ dateKey: key, ...value }));
}

function formatDateHeader(date) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const dateStr = date.toDateString();

    if (dateStr === today.toDateString()) {
        return 'Today';
    }
    if (dateStr === tomorrow.toDateString()) {
        return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    });
}

function formatTime(timeStr) {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
}

export default function EventCalendar({ filters, filterOptions }) {
    const opportunityApi = useOpportunityApi();

    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [regionFilter, setRegionFilter] = useState(filters.region || '');
    const [typeFilter, setTypeFilter] = useState('');

    // Event types that show in calendar
    const eventTypes = [
        'Workshop',
        'Training Session',
        'Webinar',
        'Seminar',
        'Networking Event',
        'Conference',
        'Career Fair',
        'Open House',
        'Competition',
        'Hackathon',
        'Showcase/Expo',
    ];

    useEffect(() => {
        const loadEvents = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = {
                    limit: 50,
                };
                if (regionFilter) params.region = regionFilter;
                if (typeFilter) params.type = typeFilter;

                const result = await opportunityApi.fetchCalendar(params);
                setEvents(result.events || []);
            } catch (err) {
                console.error('Failed to load events:', err);
                setError('Failed to load events. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        loadEvents();
    }, [opportunityApi, regionFilter, typeFilter]);

    const groupedEvents = groupEventsByDate(events);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading events...</p>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div className="event-calendar">
            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Event Type
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={typeFilter}
                                    onChange={(e) =>
                                        setTypeFilter(e.target.value)
                                    }
                                >
                                    <option value="">All Event Types</option>
                                    {eventTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small text-muted">
                                    Region
                                </Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={regionFilter}
                                    onChange={(e) =>
                                        setRegionFilter(e.target.value)
                                    }
                                >
                                    <option value="">All Regions</option>
                                    {filterOptions.regions?.map((region) => (
                                        <option key={region} value={region}>
                                            {region}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Events List */}
            {groupedEvents.length === 0 ? (
                <div className="text-center py-5">
                    <div className="display-1 mb-3">📅</div>
                    <h4>No upcoming events</h4>
                    <p className="text-muted">
                        Check back soon for new events in the ecosystem.
                    </p>
                </div>
            ) : (
                groupedEvents.map(({ dateKey, date, events: dayEvents }) => (
                    <div key={dateKey} className="mb-4">
                        <h5 className="calendar-date-header">
                            {formatDateHeader(date)}
                        </h5>

                        {dayEvents.map((event) => (
                            <Card
                                key={event._id}
                                as={Link}
                                to={`/opportunities/${event._id}`}
                                className="calendar-event-item text-decoration-none mb-3 border-0 shadow-sm"
                            >
                                <Card.Body className="py-3">
                                    <Row className="align-items-center">
                                        <Col xs={12} md={8}>
                                            <div className="d-flex align-items-start gap-3">
                                                <div className="text-center">
                                                    <div className="text-primary fw-bold">
                                                        {date.toLocaleDateString(
                                                            'en-US',
                                                            { month: 'short' }
                                                        )}
                                                    </div>
                                                    <div className="display-6 text-primary">
                                                        {date.getDate()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <h6 className="mb-1 text-primary">
                                                        {event.title}
                                                    </h6>
                                                    <p className="text-muted small mb-1">
                                                        {
                                                            event.organization
                                                                ?.name
                                                        }
                                                    </p>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        <Badge
                                                            bg="info"
                                                            className="small"
                                                        >
                                                            {event.type}
                                                        </Badge>
                                                        {event.location
                                                            ?.isRemote && (
                                                            <Badge
                                                                bg="light"
                                                                text="dark"
                                                                className="small"
                                                            >
                                                                🌐 Virtual
                                                            </Badge>
                                                        )}
                                                        {event.location
                                                            ?.city && (
                                                            <Badge
                                                                bg="light"
                                                                text="dark"
                                                                className="small"
                                                            >
                                                                📍{' '}
                                                                {
                                                                    event
                                                                        .location
                                                                        .city
                                                                }
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col
                                            xs={12}
                                            md={4}
                                            className="text-md-end mt-3 mt-md-0"
                                        >
                                            {event.event?.startTime && (
                                                <div className="calendar-event-time">
                                                    🕐{' '}
                                                    {formatTime(
                                                        event.event.startTime
                                                    )}
                                                    {event.event?.endTime &&
                                                        ` - ${formatTime(event.event.endTime)}`}
                                                </div>
                                            )}
                                            {event.event
                                                ?.registrationRequired && (
                                                <Badge
                                                    bg="warning"
                                                    text="dark"
                                                    className="mt-2"
                                                >
                                                    Registration Required
                                                </Badge>
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                ))
            )}
        </div>
    );
}

EventCalendar.propTypes = {
    filters: PropTypes.shape({
        region: PropTypes.string,
    }),
    filterOptions: PropTypes.shape({
        regions: PropTypes.arrayOf(PropTypes.string),
    }),
};

EventCalendar.defaultProps = {
    filters: {},
    filterOptions: {},
};
